import React, { useEffect, useState } from "react";
import { NavbarAdmin } from "../components/NavbarAdmin";
import { useLanguage } from "../context/LanguageContext";
import { t } from "../i18n";

const API = process.env.REACT_APP_API_URL || "";

const STATUS_CFG = {
  Pending:  { labelKey: "cancPending",  bg: "#fef3c7", color: "#92400e", dot: "#f59e0b" },
  Approved: { labelKey: "cancApproved", bg: "#d1fae5", color: "#065f46", dot: "#10b981" },
  Rejected: { labelKey: "cancRejected", bg: "#fee2e2", color: "#991b1b", dot: "#ef4444" },
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("th-TH", { day: "2-digit", month: "short", year: "numeric" }) : "-";

const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString("th-TH", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";

/**
 * Props:
 *   showNavbar  boolean  (default: true)
 *   ถ้า false → ไม่แสดง NavbarAdmin (ใช้เมื่ออยู่ใน StaffLayout ที่มี NavbarStaff แล้ว)
 */
export default function AdminCancellationPage({ showNavbar = true }) {
  const { lang } = useLanguage();
  const [requests,   setRequests]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [filter,     setFilter]     = useState("Pending");
  const [selected,   setSelected]   = useState(null);
  const [refund,     setRefund]     = useState("");
  const [note,       setNote]       = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitErr,  setSubmitErr]  = useState("");

  // ── แอดมินยกเลิกเอง ──
  const [showActive,    setShowActive]    = useState(false);   // modal เลือกการจอง
  const [activeList,    setActiveList]    = useState([]);
  const [activeLoading, setActiveLoading] = useState(false);
  const [activeErr,     setActiveErr]     = useState("");
  const [toCancel,      setToCancel]      = useState(null);    // booking ที่กำลังยืนยันยกเลิก
  const [admReason,     setAdmReason]     = useState("");
  const [admRefund,     setAdmRefund]     = useState("");
  const [admNote,       setAdmNote]       = useState("");
  const [cancelling,    setCancelling]    = useState(false);
  const [cancelErr,     setCancelErr]     = useState("");

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/api/admin/cancellations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(t(lang, "cancLoadFail"));
      const data = await res.json();
      setRequests(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, []);

  const openModal = (req) => {
    setSelected(req);
    setRefund(req.refundAmount ?? "");
    setNote("");
    setSubmitErr("");
  };

  const handleAction = async (action) => {
    try {
      setSubmitting(true);
      setSubmitErr("");
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/api/admin/cancellations/${selected._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          status: action,
          refundAmount: Number(refund) || 0,
          adminNote: note,
        }),
      });
      if (!res.ok) throw new Error(t(lang, "cancActionFail"));
      setSelected(null);
      fetchRequests();
    } catch (err) {
      setSubmitErr(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // เปิด modal + โหลดรายการจองที่ยัง active
  const openActiveModal = async () => {
    setShowActive(true);
    setToCancel(null);
    setActiveErr("");
    try {
      setActiveLoading(true);
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/api/admin/active-bookings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(t(lang, "cancLoadFail"));
      setActiveList(await res.json());
    } catch (err) {
      setActiveErr(err.message);
    } finally {
      setActiveLoading(false);
    }
  };

  const pickToCancel = (b) => {
    setToCancel(b);
    setAdmReason("");
    setAdmRefund("");
    setAdmNote("");
    setCancelErr("");
  };

  const handleAdminCancel = async () => {
    try {
      setCancelling(true);
      setCancelErr("");
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/api/admin/bookings/${toCancel._id}/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          reason: admReason,
          refundAmount: Number(admRefund) || 0,
          adminNote: admNote,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || t(lang, "cancActionFail"));
      }
      setShowActive(false);
      setToCancel(null);
      fetchRequests();
    } catch (err) {
      setCancelErr(err.message);
    } finally {
      setCancelling(false);
    }
  };

  const filtered = requests.filter(r => r.status === filter);

  const counts = {
    Pending:  requests.filter(r => r.status === "Pending").length,
    Approved: requests.filter(r => r.status === "Approved").length,
    Rejected: requests.filter(r => r.status === "Rejected").length,
  };

  return (
    <div style={styles.page}>
      {/* ── แสดง NavbarAdmin เฉพาะเมื่อ showNavbar=true (admin route) ── */}
      {showNavbar && <NavbarAdmin />}

      <main style={styles.main}>
        <div style={styles.pageHeader}>
          <div>
            <h1 style={styles.pageTitle}>{t(lang, "cancTitle")}</h1>
            <p style={styles.pageSub}>{t(lang, "cancSub")}</p>
          </div>
          <button style={styles.newCancelBtn} onClick={openActiveModal}>
            + {t(lang, "cancAdminNewBtn")}
          </button>
        </div>

        {/* Summary cards */}
        <div style={styles.summaryRow}>
          {[
            { key: "Pending",  label: t(lang, "cancPending"),  bg: "#fef3c7", color: "#92400e" },
            { key: "Approved", label: t(lang, "cancApproved"), bg: "#d1fae5", color: "#065f46" },
            { key: "Rejected", label: t(lang, "cancRejected"), bg: "#fee2e2", color: "#991b1b" },
          ].map(s => (
            <div
              key={s.key}
              style={{ ...styles.summaryCard, background: s.bg, cursor: "pointer", outline: filter === s.key ? `2px solid ${s.color}` : "none" }}
              onClick={() => setFilter(s.key)}
            >
              <p style={{ margin: 0, fontSize: 13, color: s.color, fontWeight: 600 }}>{s.label}</p>
              <p style={{ margin: "4px 0 0", fontSize: 28, fontWeight: 700, color: s.color }}>{counts[s.key]}</p>
            </div>
          ))}
        </div>

        {/* Table card */}
        <div style={styles.card}>
          {loading && <div style={styles.center}><p style={{ color: "#6b7280" }}>{t(lang, "cancLoading")}</p></div>}
          {error   && <div style={styles.center}><p style={{ color: "#ef4444" }}>⚠️ {error}</p></div>}

          {!loading && !error && filtered.length === 0 && (
            <div style={styles.center}>
              <p style={{ color: "#9ca3af", fontSize: 14 }}>{t(lang, "cancNoItems")}</p>
            </div>
          )}

          {!loading && !error && filtered.length > 0 && (
            <div style={{ overflowX: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.thead}>
                  <th style={styles.th}>{t(lang, "cancColGuest")}</th>
                  <th style={styles.th}>{t(lang, "cancColRoom")}</th>
                  <th style={styles.th}>{t(lang, "cancColCheckIn")}</th>
                  <th style={styles.th}>{t(lang, "cancColTotal")}</th>
                  <th style={styles.th}>{t(lang, "cancColRequested")}</th>
                  <th style={styles.th}>{t(lang, "cancColStatus")}</th>
                  <th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((req) => {
                  const b   = req.bookingId;
                  const cfg = STATUS_CFG[req.status] || STATUS_CFG.Pending;
                  return (
                    <tr key={req._id} style={styles.tr}
                      onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <td style={styles.td}>{b?.guestName || "-"}</td>
                      <td style={styles.td}>
                        {b?.roomType?.name || "-"} {b?.roomUnit?.roomNumber ? `: ${b.roomUnit.roomNumber}` : ""}
                      </td>
                      <td style={styles.td}>{fmtDate(b?.checkIn)}</td>
                      <td style={styles.td}>
                        {b?.totalAmount != null ? `฿${b.totalAmount.toLocaleString()}` : "-"}
                      </td>
                      <td style={styles.td}>{fmtDateTime(req.createdAt)}</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, background: cfg.bg, color: cfg.color }}>
                          <span style={{ ...styles.dot, background: cfg.dot }} />
                          {t(lang, cfg.labelKey)}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <button style={styles.detailBtn} onClick={() => openModal(req)}>
                          {req.status === "Pending" ? t(lang, "cancReview") : t(lang, "cancViewDetail")}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </main>

      {/* Modal */}
      {selected && (
        <div style={modal.overlay}
          onMouseDown={(e) => { if (e.target === e.currentTarget) e.currentTarget._ok = true; }}
          onMouseUp={(e) => { if (e.currentTarget._ok && e.target === e.currentTarget) setSelected(null); e.currentTarget._ok = false; }}
        >
          <div style={modal.box} onClick={e => e.stopPropagation()}>
            <div style={modal.header}>
              <h2 style={modal.title}>{t(lang, "cancModalTitle")}</h2>
              <button style={modal.closeBtn} onClick={() => setSelected(null)}>✕</button>
            </div>

            <div style={modal.divider} />

            <div style={modal.section}>
              <p style={modal.sectionLabel}>{t(lang, "cancBookingInfo")}</p>
              <div style={modal.infoGrid}>
                <InfoRow label={t(lang, "cancGuest")}    value={selected.bookingId?.guestName || "-"} />
                <InfoRow label={t(lang, "cancRoom")}     value={`${selected.bookingId?.roomType?.name || "-"} : ${selected.bookingId?.roomUnit?.roomNumber || "-"}`} />
                <InfoRow label={t(lang, "cancCheckIn")}  value={fmtDate(selected.bookingId?.checkIn)} />
                <InfoRow label={t(lang, "cancCheckOut")} value={fmtDate(selected.bookingId?.checkOut)} />
                <InfoRow label={t(lang, "cancTotal")}    value={selected.bookingId?.totalAmount != null ? `฿${selected.bookingId.totalAmount.toLocaleString()}` : "-"} bold />
              </div>
            </div>

            <div style={modal.divider} />

            <div style={modal.section}>
              <p style={modal.sectionLabel}>{t(lang, "cancReason")}</p>
              <div style={modal.reasonBox}>
                <p style={{ margin: 0, fontSize: 14, color: "#374151", lineHeight: 1.7 }}>
                  {selected.reason || t(lang, "cancNoReason")}
                </p>
              </div>
            </div>

            {selected.status === "Pending" && (
              <>
                <div style={modal.divider} />
                <div style={modal.section}>
                  <p style={modal.sectionLabel}>{t(lang, "cancRefund")}</p>
                  <input type="number" min="0" style={modal.input} placeholder="0"
                    value={refund} onChange={e => setRefund(e.target.value)} />
                </div>
                <div style={modal.section}>
                  <p style={modal.sectionLabel}>{t(lang, "cancNote")}</p>
                  <textarea rows={3} style={modal.textarea}
                    placeholder={t(lang, "cancNotePh")}
                    value={note} onChange={e => setNote(e.target.value)} />
                </div>
                {submitErr && <p style={{ margin: "0 0 12px", fontSize: 13, color: "#ef4444" }}>{submitErr}</p>}
                <div style={modal.btnRow}>
                  <button style={{ ...modal.btnBase, ...modal.btnGhost }} onClick={() => setSelected(null)} disabled={submitting}>{t(lang, "cancCancelBtn")}</button>
                  <button style={{ ...modal.btnBase, ...modal.btnGray  }} onClick={() => handleAction("Rejected")} disabled={submitting}>{t(lang, "cancRejectBtn")}</button>
                  <button style={{ ...modal.btnBase, ...modal.btnGreen }} onClick={() => handleAction("Approved")} disabled={submitting}>
                    {submitting ? t(lang, "cancProcessing") : t(lang, "cancApproveBtn")}
                  </button>
                </div>
              </>
            )}

            {selected.status !== "Pending" && (
              <>
                <div style={modal.divider} />
                <div style={modal.section}>
                  <p style={modal.sectionLabel}>{t(lang, "cancResult")}</p>
                  <div style={modal.infoGrid}>
                    <InfoRow label={t(lang, "cancDecisionStatus")} value={t(lang, STATUS_CFG[selected.status]?.labelKey)} color={STATUS_CFG[selected.status]?.color} />
                    <InfoRow label={t(lang, "cancRefundAmt")}      value={selected.refundAmount ? `฿${selected.refundAmount.toLocaleString()}` : t(lang, "cancNoRefund")} />
                    <InfoRow label={t(lang, "cancActionDate")}     value={fmtDateTime(selected.cancelledAt)} />
                    {selected.adminNote && <InfoRow label={t(lang, "cancNoteLabel")} value={selected.adminNote} />}
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                  <button style={{ ...modal.btnBase, ...modal.btnGhost }} onClick={() => setSelected(null)}>{t(lang, "cancClose")}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal: แอดมินเลือกการจองมายกเลิกเอง */}
      {showActive && (
        <div style={modal.overlay}
          onMouseDown={(e) => { if (e.target === e.currentTarget) e.currentTarget._ok = true; }}
          onMouseUp={(e) => { if (e.currentTarget._ok && e.target === e.currentTarget) setShowActive(false); e.currentTarget._ok = false; }}
        >
          <div style={modal.box} onClick={e => e.stopPropagation()}>
            {!toCancel ? (
              <>
                <div style={modal.header}>
                  <div>
                    <h2 style={modal.title}>{t(lang, "cancAdminTitle")}</h2>
                    <p style={{ margin: "4px 0 0", fontSize: 13, color: "#9ca3af" }}>{t(lang, "cancAdminSub")}</p>
                  </div>
                  <button style={modal.closeBtn} onClick={() => setShowActive(false)}>✕</button>
                </div>
                <div style={modal.divider} />

                {activeLoading && <p style={{ textAlign: "center", color: "#6b7280", padding: "24px 0" }}>{t(lang, "cancLoading")}</p>}
                {activeErr && <p style={{ textAlign: "center", color: "#ef4444", padding: "24px 0" }}>⚠️ {activeErr}</p>}
                {!activeLoading && !activeErr && activeList.length === 0 && (
                  <p style={{ textAlign: "center", color: "#9ca3af", padding: "24px 0", fontSize: 14 }}>{t(lang, "cancAdminNoActive")}</p>
                )}

                {!activeLoading && !activeErr && activeList.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: "50vh", overflowY: "auto" }}>
                    {activeList.map((b) => {
                      const isIn = b.status === "CheckedIn";
                      return (
                        <div key={b._id} style={activeRow.item}>
                          <div style={{ minWidth: 0 }}>
                            <p style={activeRow.name}>{b.guestName || b.user?.name || "-"}</p>
                            <p style={activeRow.meta}>
                              {(b.roomType?.name || "-")}{b.roomUnit?.roomNumber ? ` : ${b.roomUnit.roomNumber}` : ""} · {fmtDate(b.checkIn)}
                            </p>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                            <span style={{ ...activeRow.badge, background: isIn ? "#dbeafe" : "#fef3c7", color: isIn ? "#1e40af" : "#92400e" }}>
                              {t(lang, isIn ? "cancAdminCheckedIn" : "cancAdminBooked")}
                            </span>
                            <button style={activeRow.pickBtn} onClick={() => pickToCancel(b)}>{t(lang, "cancAdminPick")}</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={modal.header}>
                  <h2 style={modal.title}>{t(lang, "cancAdminConfirmTitle")}</h2>
                  <button style={modal.closeBtn} onClick={() => setToCancel(null)}>✕</button>
                </div>
                <div style={modal.divider} />

                <div style={modal.section}>
                  <p style={modal.sectionLabel}>{t(lang, "cancBookingInfo")}</p>
                  <div style={modal.infoGrid}>
                    <InfoRow label={t(lang, "cancGuest")}   value={toCancel.guestName || toCancel.user?.name || "-"} />
                    <InfoRow label={t(lang, "cancRoom")}    value={`${toCancel.roomType?.name || "-"}${toCancel.roomUnit?.roomNumber ? ` : ${toCancel.roomUnit.roomNumber}` : ""}`} />
                    <InfoRow label={t(lang, "cancCheckIn")} value={fmtDate(toCancel.checkIn)} />
                    <InfoRow label={t(lang, "cancTotal")}   value={toCancel.totalAmount != null ? `฿${toCancel.totalAmount.toLocaleString()}` : "-"} bold />
                  </div>
                </div>

                <div style={modal.divider} />

                <div style={modal.section}>
                  <p style={modal.sectionLabel}>{t(lang, "cancAdminReason")}</p>
                  <textarea rows={2} style={modal.textarea} placeholder={t(lang, "cancAdminReasonPh")}
                    value={admReason} onChange={e => setAdmReason(e.target.value)} />
                </div>
                <div style={modal.section}>
                  <p style={modal.sectionLabel}>{t(lang, "cancRefund")}</p>
                  <input type="number" min="0" style={modal.input} placeholder="0"
                    value={admRefund} onChange={e => setAdmRefund(e.target.value)} />
                </div>
                <div style={modal.section}>
                  <p style={modal.sectionLabel}>{t(lang, "cancNote")}</p>
                  <textarea rows={2} style={modal.textarea} placeholder={t(lang, "cancNotePh")}
                    value={admNote} onChange={e => setAdmNote(e.target.value)} />
                </div>

                {cancelErr && <p style={{ margin: "0 0 12px", fontSize: 13, color: "#ef4444" }}>{cancelErr}</p>}
                <div style={modal.btnRow}>
                  <button style={{ ...modal.btnBase, ...modal.btnGhost }} onClick={() => setToCancel(null)} disabled={cancelling}>{t(lang, "cancCancelBtn")}</button>
                  <button style={{ ...modal.btnBase, ...modal.btnRed }} onClick={handleAdminCancel} disabled={cancelling}>
                    {cancelling ? t(lang, "cancProcessing") : t(lang, "cancAdminConfirmBtn")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value, bold, color }) {
  return (
    <div style={{ display: "flex", gap: 8, padding: "5px 0", borderBottom: "1px solid #f3f4f6" }}>
      <span style={{ fontSize: 13, color: "#9ca3af", minWidth: 110 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: bold ? 700 : 500, color: color || "#111827" }}>{value}</span>
    </div>
  );
}

const styles = {
  page:        { minHeight: "100vh", background: "#f0f0f0", fontFamily: "'Noto Sans Thai', sans-serif", display: "flex", flexDirection: "column" },
  main:        { flex: 1, padding: "24px clamp(16px, 4vw, 32px) 40px", maxWidth: 1100, margin: "0 auto", width: "100%", boxSizing: "border-box" },
  pageHeader:  { marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" },
  newCancelBtn:{ background: "#dc2626", color: "#fff", border: "none", borderRadius: 10, padding: "10px 18px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 },
  pageTitle:   { margin: 0, fontSize: 24, fontWeight: 800, color: "#111" },
  pageSub:     { margin: "4px 0 0", fontSize: 14, color: "#6b7280" },
  summaryRow:  { display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" },
  summaryCard: { flex: "1 1 140px", borderRadius: 14, padding: "16px 20px" },
  card:        { background: "#fff", borderRadius: 16, padding: "0", overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,.06)" },
  center:      { display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 0" },
  table:       { width: "100%", borderCollapse: "collapse" },
  thead:       { background: "#f9fafb" },
  th:          { padding: "12px 16px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #f3f4f6" },
  tr:          { transition: "background .1s", cursor: "default" },
  td:          { padding: "14px 16px", fontSize: 13, color: "#374151", borderBottom: "1px solid #f9fafb" },
  badge:       { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 999 },
  dot:         { width: 6, height: 6, borderRadius: "50%", flexShrink: 0 },
  detailBtn:   { background: "none", border: "1px solid #e5e7eb", borderRadius: 8, padding: "5px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#374151" },
};

const modal = {
  overlay:      { position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
  box:          { background: "#fff", borderRadius: 20, padding: "28px 28px 24px", width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,.2)" },
  header:       { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  title:        { margin: 0, fontSize: 18, fontWeight: 700, color: "#111" },
  closeBtn:     { background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#9ca3af", padding: "4px 8px" },
  divider:      { height: 1, background: "#f3f4f6", margin: "16px 0" },
  section:      { marginBottom: 4 },
  sectionLabel: { margin: "0 0 8px", fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" },
  infoGrid:     { display: "flex", flexDirection: "column" },
  reasonBox:    { background: "#f9fafb", borderRadius: 10, padding: "12px 14px", border: "1px solid #f3f4f6" },
  input:        { width: "100%", borderRadius: 10, border: "1px solid #e5e7eb", padding: "9px 12px", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" },
  textarea:     { width: "100%", borderRadius: 10, border: "1px solid #e5e7eb", padding: "9px 12px", fontSize: 14, fontFamily: "inherit", resize: "vertical", outline: "none", boxSizing: "border-box" },
  btnRow:       { display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 },
  btnBase:      { borderRadius: 10, padding: "10px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer", border: "none" },
  btnGhost:     { background: "none", border: "1px solid #e5e7eb", color: "#374151" },
  btnGray:      { background: "#f3f4f6", color: "#374151" },
  btnGreen:     { background: "#10b981", color: "#fff" },
  btnRed:       { background: "#dc2626", color: "#fff" },
};

const activeRow = {
  item:    { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "12px 14px", border: "1px solid #f3f4f6", borderRadius: 12, background: "#fafafa" },
  name:    { margin: 0, fontSize: 14, fontWeight: 600, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  meta:    { margin: "2px 0 0", fontSize: 12, color: "#6b7280" },
  badge:   { fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 999, whiteSpace: "nowrap" },
  pickBtn: { background: "#fff", border: "1px solid #fca5a5", color: "#dc2626", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" },
};